import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";

import Header from "./components/Header";
import {BrowserRouter as Router, Redirect, Route, Switch, matchPath, useLocation, useHistory} from "react-router-dom";
import {ToastContainer} from "react-toastify";
import AuthContext, {AuthProvider} from "./contexts/AuthContext";
import PageLoader from "./components/PageLoader";
import SuperAdminUsersList from "./pages/superadmin/SuperAdminUsersList";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { PreviewHiddenProvider } from "./contexts/PreviewHiddenContext";
import ErrorBoundary from "./components/ErrorBoundary";
import BackendGate from "./components/BackendGate";
import useScrollbarGutter from "./hooks/useScrollbarGutter";

// Synchronous static imports to completely eliminate ChunkLoadError and mobile cache mismatch
import CreateNewPassword from "./pages/CreateNewPassword";
import UnauthorizedRoute from "./utils/UnauthorizedRoute";
import AuthorizedRoute from "./utils/AuthorizedRoute";

import Login from "./pages/Login";
import SignUp from "./pages/SignUp";
import VerifyEmail from "./pages/VerifyEmail";
import SecurityCode from "./pages/SecurityCode";
import RecoveryPassword from "./pages/RecoveryPassword";
import Error500 from "./pages/Error500";
import NotFound404 from "./pages/NotFound404";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import BackToTopButton from "./components/BackToTopButton";
import TournamentsList from "./pages/tournaments/TournamentsList";
import ClubsList from "./pages/clubs/ClubsList";
import PlayerBaseList from "./pages/players/PlayerBaseList";
import PlayersList from "./pages/players/PlayersList";
import PlayersDirectory from "./pages/players/PlayersDirectory";
import PlayerDetail from "./pages/players/PlayerDetail";
import LocationsList from "./pages/locations/LocationsList";
import Tournament from "./pages/tournaments/Tournament";
import TournamentPreviewFrame from "./pages/tournaments/TournamentPreviewFrame";
import RatingList from "./pages/ratings/RatingList";
import RatingTable from "./pages/ratings/RatingTable";
import PlayerProfileModal from "./pages/players/PlayerProfileModal";
import ClubProfileModal from "./pages/clubs/ClubProfileModal";
import LocationProfileModal from "./pages/locations/LocationProfileModal";
import Footer from "./components/Footer";
import Balance from "./pages/Balance";

// the browser's own scroll-position restoration on back/forward fights with
// our React-driven restoration (e.g. TournamentsList scrolling to a specific
// card): it reapplies the raw pixel offset from before navigating away,
// which no longer lines up once content has re-rendered, causing a visible
// jump right after our own scroll already landed correctly. Routes that
// need scroll restored are responsible for doing it themselves.
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
}

const TOURNAMENT_DETAIL_PATHS = ["/tournaments/:id"];

function AppRoutes({ isPreviewFrame }) {
    const { user } = useContext(AuthContext);
    const location = useLocation();
    const history = useHistory();
    const detailMatch = matchPath(location.pathname, { path: TOURNAMENT_DETAIL_PATHS, exact: true });
    const isAdminMode = new URLSearchParams(location.search).get("admin") === "1";
    const isDetail = !!detailMatch;

    // Soft-navigation side of TournamentPreviewFrame.js's postMessage trick -
    // only relevant to the framed copy of the app (isPreviewFrame). Without
    // this, the only way to move that already-loaded iframe to a new tab
    // was changing its `src`, which is always a hard navigation (full JS
    // reboot + refetch) - visibly reloading and flashing through whatever
    // tab the fresh boot defaults to (Information) before landing on the
    // actually-requested one. `history.replace` here is a normal client-side
    // route change, indistinguishable from the user tapping a tab inside
    // the iframe directly.
    //
    // lastFromParentRef records exactly what this handler just applied, so
    // the "mirror every change back to the parent" effect below can tell a
    // genuine internal navigation (a tab tapped inside the preview) apart
    // from the mere echo of what the parent just sent - without that check,
    // any URL some in-frame effect *validates* right back to what it
    // already was (e.g. TournamentGrids' own gridTab-sync effect re-asserting
    // the same value) still counts as "location changed" to this effect,
    // gets mirrored out, the parent mirrors it back in, and the two sides
    // bounce the same change back and forth forever - the outer address bar
    // visibly flickering.
    const lastFromParentRef = useRef(null);
    useEffect(() => {
        if (!isPreviewFrame) return;
        const handler = (event) => {
            if (event.origin !== window.location.origin) return;
            if (event.data?.type !== "preview-navigate") return;
            lastFromParentRef.current = `${event.data.pathname}${event.data.search}`;
            history.replace({ pathname: event.data.pathname, search: event.data.search });
        };
        window.addEventListener("message", handler);
        return () => window.removeEventListener("message", handler);
    }, [isPreviewFrame, history]);

    // Reverse direction of the above: a stage/place tab tapped *inside* the
    // preview (TournamentDrawStages.js/TournamentSchedule.js etc, each doing
    // their own `history.replace({search: ...})`) only ever updated this
    // framed copy's own location - the outer, real page's URL never heard
    // about it. That's harmless while the preview stays open (this framed
    // instance already reflects the new selection correctly), but the outer
    // page's own hidden <Tournament> - the one that reappears once the user
    // switches back to Mobile/off - was still sitting on whatever stage was
    // selected *before* the preview was ever opened, since it never
    // remounts and nothing else told it to update. Mirroring every location
    // change back out to the parent keeps the outer URL (and that hidden
    // instance's own state, which re-derives from it) in sync the whole
    // time, not just at the moment the preview was first opened.
    useEffect(() => {
        if (!isPreviewFrame) return;
        const current = `${location.pathname}${location.search}`;
        if (lastFromParentRef.current === current) {
            lastFromParentRef.current = null;
            return;
        }
        window.parent.postMessage(
            { type: "preview-location-changed", pathname: location.pathname, search: location.search },
            window.location.origin
        );
    }, [isPreviewFrame, location.pathname, location.search]);

    // Pinch-zoom stays on for public/guest viewers (they only ever have the
    // odd search box, and may want to zoom into a bracket/schedule table on
    // a small phone) - the same admin editor screens that motivated locking
    // it down (iOS auto-zooming on every small-font input focus) only run
    // when this same isAdminMode/user combo also drives Tournament's own
    // is_authorized prop below, so mirror that condition here.
    const isAdminView = isAdminMode && !!user;
    useEffect(() => {
        const viewport = document.querySelector('meta[name="viewport"]');
        if (!viewport) return;
        viewport.setAttribute("content", isAdminView
            ? "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover"
            : "width=device-width, initial-scale=1.0, minimum-scale=0.5, maximum-scale=2.0, user-scalable=yes, viewport-fit=cover"
        );
    }, [isAdminView]);

    // Header.js's "Screen size" picker (Public View only) - see
    // TournamentPreviewFrame.js for why this swaps in a same-origin iframe
    // instead of just resizing something with CSS. Forced off inside a
    // preview iframe itself: it's the same origin, so left alone it would
    // read this exact same localStorage-backed value straight back out of
    // ThemeContext and recurse into another nested preview forever.
    const { previewWidth: rawPreviewWidth } = useTheme();
    const previewWidth = isPreviewFrame ? 0 : rawPreviewWidth;
    const showPreviewFrame = previewWidth > 0 && !isAdminView;

    // <Tournament> is NOT optional here even while the preview iframe is
    // what's actually showing (display:none below) - Header.js's own
    // tournament name/club/tabs read tournament data purely off the
    // tournamentSubscribers bus, which only the real <Tournament> instance
    // ever publishes to (the framed copy inside TournamentPreviewFrame runs
    // in the iframe's own separate JS realm, with its own separate copy of
    // that bus - it can never reach the outer Header). Skipping this mount
    // whenever a preview was already active before the very first render
    // (previewWidth restored from localStorage, or the site's own default
    // desktop-detection) left Header's tournament stuck on its skeleton
    // forever - nothing left to ever feed it - until the user happened to
    // touch the zoom control, which incidentally forced a re-render that
    // fixed it as a side effect. TournamentPreviewFrame, on the other hand,
    // genuinely only needs to exist once previewWidth has actually been
    // used at least once - hasUsedPreview keeps it mounted from then on
    // (toggled by CSS display below) instead of unmounting/remounting on
    // every subsequent toggle (full refetch, lost scroll/sub-tab state, a
    // visible reload) - the same "no reload" property the Admin/Public
    // mode-switch already has by staying on one stable Tournament instance
    // and just changing its is_authorized prop. lastPreviewWidth keeps
    // feeding it a valid width while hidden at previewWidth:0, so it
    // doesn't re-layout at width 0 the moment it's shown again.
    const [hasUsedPreview, setHasUsedPreview] = useState(previewWidth > 0);
    const [lastPreviewWidth, setLastPreviewWidth] = useState(previewWidth || 768);
    useEffect(() => {
        if (previewWidth > 0) {
            setHasUsedPreview(true);
            setLastPreviewWidth(previewWidth);
        }
    }, [previewWidth]);

    const [background, setBackground] = useState(() => {
        if (!detailMatch) return location;
        return { ...location, pathname: "/tournaments", search: "" };
    });
    useEffect(() => {
        if (!detailMatch) setBackground(location);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detailMatch, location.pathname, location.search]);

    const effectiveBackground = detailMatch ? background : location;

    const backgroundRef = useRef(null);
    const savedScrollRef = useRef(0);
    // Tracks effectiveBackground.pathname across renders so the effect below
    // can tell "returning from the detail overlay to the same list" (restore
    // its saved scroll) apart from "navigated to a genuinely different admin
    // section" (Tournaments -> Clubs etc, still isDetail:false throughout,
    // so it never changes isDetail either) - manual scrollRestoration above
    // means nothing else resets scroll on that second case, so it silently
    // opened wherever the previous section's scroll happened to be.
    const prevPathnameRef = useRef(effectiveBackground.pathname);

    useLayoutEffect(() => {
        const el = backgroundRef.current;
        if (el) {
            if (isDetail) {
                savedScrollRef.current = window.scrollY;
                el.style.display = "none";
            } else {
                el.style.display = "";
            }
        }
        if (isDetail || prevPathnameRef.current !== effectiveBackground.pathname) {
            window.scrollTo(0, 0);
        } else {
            window.scrollTo(0, savedScrollRef.current);
        }
        prevPathnameRef.current = effectiveBackground.pathname;
    }, [isDetail, effectiveBackground.pathname]);

    return (
        // marginTop:0 overrides .main's own 85px (and the tournament-mode
        // sibling-selector variant, which would only apply if a real Header
        // existed here anyway) - a preview iframe has no Header/BottomNav of
        // its own, only Tournament's raw content, so there's no header
        // height to clear.
        <div className="main" style={isPreviewFrame ? { marginTop: 0 } : undefined}>
            <ErrorBoundary>
                <BackendGate>
                    <div ref={backgroundRef}>
                        <Switch location={effectiveBackground}>
                            {/* Home used to render its own TournamentsList copy at "/" - same
                                content as /tournaments, but the header/nav match on real hrefs
                                (see MainHeader.js's menuItems/matchHref), so "/" itself never read
                                as the Tournaments section: no active nav highlight, no header
                                title. Redirecting outright keeps that copy from existing at all. */}
                            <Route exact path='/'>
                                <Redirect to="/tournaments" />
                            </Route>
                            <Route exact path='/tournaments'>
                                <TournamentsList is_authorized={false}/>
                            </Route>
                            <Route exact path='/clubs'>
                                <ClubsList is_authorized={false}/>
                            </Route>
                            <Route exact path='/players'>
                                <PlayersDirectory/>
                            </Route>
                            <Route exact path='/locations'>
                                <LocationsList is_authorized={false}/>
                            </Route>
                            <AuthorizedRoute exact path='/admin/tournaments'>
                                <TournamentsList/>
                            </AuthorizedRoute>
                            <Route exact path='/admin/tournaments/:id' render={({ match, location: redirectLocation }) => {
                                const params = new URLSearchParams(redirectLocation.search);
                                params.set("admin", "1");
                                return <Redirect to={{ pathname: `/tournaments/${match.params.id}`, search: params.toString() }} />;
                            }} />
                            <AuthorizedRoute exact path='/admin/clubs'>
                                <ClubsList/>
                            </AuthorizedRoute>
                            <AuthorizedRoute exact path='/admin/bases/'>
                                <PlayerBaseList/>
                            </AuthorizedRoute>
                            <AuthorizedRoute exact path='/admin/ratings/'>
                                <RatingList/>
                            </AuthorizedRoute>
                            <AuthorizedRoute exact path='/admin/ratings/:id'>
                                <RatingTable/>
                            </AuthorizedRoute>
                            <AuthorizedRoute exact path='/admin/bases/:id/players'>
                                <PlayersList/>
                            </AuthorizedRoute>
                            <AuthorizedRoute exact path="/admin/bases/:baseId/players/:playerId">
                                <PlayerDetail/>
                            </AuthorizedRoute>
                            <AuthorizedRoute exact path='/admin/locations'>
                                <LocationsList/>
                            </AuthorizedRoute>
                            <AuthorizedRoute exact path='/balance'>
                                <Balance/>
                            </AuthorizedRoute>
                            <AuthorizedRoute exact path='/intennis'>
                                <SuperAdminUsersList/>
                            </AuthorizedRoute>
                            <UnauthorizedRoute path='/login'>
                                <Login/>
                            </UnauthorizedRoute>
                            <UnauthorizedRoute path='/sign-up'>
                                <SignUp/>
                            </UnauthorizedRoute>
                            <UnauthorizedRoute path='/verify-email'>
                                <VerifyEmail/>
                            </UnauthorizedRoute>
                            <UnauthorizedRoute path='/security-code'>
                                <SecurityCode/>
                            </UnauthorizedRoute>
                            <UnauthorizedRoute path='/recovery-password'>
                                <RecoveryPassword/>
                            </UnauthorizedRoute>
                            <UnauthorizedRoute path='/create-new-password'>
                                <CreateNewPassword/>
                            </UnauthorizedRoute>
                            <Route exact path='/privacy-policy'>
                                <PrivacyPolicy/>
                            </Route>
                            <Route exact path='/404'>
                                <NotFound404/>
                            </Route>
                            <Route exact path='/500'>
                                <Error500/>
                            </Route>
                            <Redirect to="/404"/>
                        </Switch>
                    </div>
                    {detailMatch && (
                        <div className="tournament-detail-root">
                            <Route exact path={detailMatch.path}>
                                <div style={{ display: showPreviewFrame ? "none" : "block" }}>
                                    <PreviewHiddenProvider value={showPreviewFrame}>
                                        <Tournament is_authorized={isAdminView}/>
                                    </PreviewHiddenProvider>
                                </div>
                                {hasUsedPreview && (
                                    <div style={{ display: showPreviewFrame ? "block" : "none" }}>
                                        <TournamentPreviewFrame previewWidth={lastPreviewWidth}/>
                                    </div>
                                )}
                            </Route>
                        </div>
                    )}
                </BackendGate>
            </ErrorBoundary>
            {!isPreviewFrame && <BackToTopButton/>}
            {!isPreviewFrame && (
                <ToastContainer
                    toastClassName="p-4 m-2"
                    autoClose={2000}/>
            )}
        </div>
    );
}

function App() {
    useScrollbarGutter();
    // Header.js's "Screen size" picker embeds a same-origin iframe pointed
    // at this same route with this param added (see TournamentPreviewFrame.
    // js) to render Tournament's content at a chosen width while the real
    // Header/BottomNav around it stay untouched - that iframe wants only
    // the content, not a second copy of the site's own chrome around it.
    const isPreviewFrame = new URLSearchParams(window.location.search).get("preview_frame") === "1";

    // Lets plain CSS (index.css's own .bottom-sheet-panel rules) key off
    // "this document is the framed copy inside the Screen size preview",
    // without threading a prop/context through every component that needs
    // it - see those rules for why BottomSheetPanel specifically cares.
    useEffect(() => {
        document.documentElement.classList.toggle("preview-frame-mode", isPreviewFrame);
    }, [isPreviewFrame]);

    return (
        <>
        <ThemeProvider>
            <Router>
                <AuthProvider>
                    <ErrorBoundary>
                        {!isPreviewFrame && <Header/>}
                        <PlayerProfileModal />
                        <ClubProfileModal />
                        <LocationProfileModal />
                        <AppRoutes isPreviewFrame={isPreviewFrame}/>
                        {!isPreviewFrame && <Footer/>}
                    </ErrorBoundary>
                </AuthProvider>
            </Router>
        </ThemeProvider>
        </>
    );
}

export default App;
